/**
 * PlacesView — Family geography.
 *
 * Two-panel layout:
 *   - Leaflet map with a pin per geocoded place. Pin radius scales with the
 *     number of mentions that fall inside the current time window, so
 *     zero-mention places disappear as the user scrubs the slider. Pin
 *     colour encodes dominant event type (birth/marriage/death/record).
 *
 *   - Side list: same places, synced to the slider so users without a
 *     geocoded pin still see the data.
 *
 * Time slider:
 *   - Two handles, span between them is the active window.
 *   - Places whose mention_years[] intersect the window stay visible.
 *   - Mentions outside the window exclude the pin from the count shown on
 *     the list card, so the slider really reflects "who lived where during
 *     this decade".
 *
 * Non-geocoded places render in the list with a grey ⊘ tag so the user
 * knows to fix ambiguous names. The background geocoder fills in new
 * places over time.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { api } from '../../services/api.js'

const EVENT_LABEL = {
  birth:           'birth',
  death:           'death',
  marriage:        'marriage',
  document_origin: 'record',
  other:           'other',
}

const EVENT_COLOR = {
  birth:           '#3b6b5a',
  death:           '#1f2937',
  marriage:        '#c8a56e',
  document_origin: '#6b7280',
  other:           '#9ca3af',
}

export default function PlacesView({ treeId }) {
  const [places, setPlaces]   = useState(null)
  const [error, setError]     = useState(null)
  const [windowStart, setWS]  = useState(null)
  const [windowEnd, setWE]    = useState(null)
  const [selectedId, setSel]  = useState(null)
  const mapRef    = useRef(null)
  const mapObjRef = useRef(null)
  const layerRef  = useRef(null)

  useEffect(() => {
    if (!treeId) return
    api.get(`/trees/${treeId}/kg/places`)
      .then(setPlaces)
      .catch(err => setError(err.message))
  }, [treeId])

  const { globalMin, globalMax } = useMemo(() => {
    if (!places?.length) return { globalMin: null, globalMax: null }
    const mins = places.map(p => p.earliest_year).filter(Boolean)
    const maxs = places.map(p => p.latest_year).filter(Boolean)
    return {
      globalMin: mins.length ? Math.min(...mins) : null,
      globalMax: maxs.length ? Math.max(...maxs) : null,
    }
  }, [places])

  // Count mentions per place inside the active time window so the map pin
  // radius and the list card count stay in sync.
  const filteredPlaces = useMemo(() => {
    if (!places) return []
    return places.map(p => {
      const years = p.mention_years || []
      const inWindow = (windowStart == null && windowEnd == null)
        ? years.length
        : years.filter(y =>
            (windowStart == null || y >= windowStart) &&
            (windowEnd   == null || y <= windowEnd)
          ).length
      return { ...p, inWindow }
    })
  }, [places, windowStart, windowEnd])

  // ── Map setup (once the map div is actually in the DOM) ─────────────────
  // Dep: `places`. On first render we return early with Loading… so mapRef
  // is null and this effect bails. When places arrives and the component
  // re-renders with the real map div, the effect fires again and the guard
  // (mapObjRef.current) prevents re-init.
  useEffect(() => {
    if (!mapRef.current || mapObjRef.current) return
    try {
      // Default centre — Netherlands. First pin-fit call recentres to data.
      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: true,
        attributionControl: true,
      }).setView([52.2, 5.3], 7)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 18,
      }).addTo(map)
      mapObjRef.current = map
      layerRef.current = L.layerGroup().addTo(map)

      // Leaflet caches container size at init. If the parent layout wasn't
      // settled yet the map renders as 0×0 grey — invalidateSize() forces
      // a re-measure. ResizeObserver keeps it correct across window resize
      // and sidebar toggles. Run once on mount, once on first animation
      // frame, and once more after a short delay to cover layouts that
      // only finalise after paint.
      const kick = () => map.invalidateSize()
      kick()
      requestAnimationFrame(kick)
      const late = setTimeout(kick, 250)
      const ro = new ResizeObserver(kick)
      ro.observe(mapRef.current)
      return () => {
        clearTimeout(late)
        ro.disconnect()
        map.remove()
        mapObjRef.current = null
      }
    } catch (err) {
      console.error('[PlacesView] Leaflet init failed:', err)
    }
  }, [places])

  // ── Render pins whenever the filtered set changes ───────────────────────
  useEffect(() => {
    const map = mapObjRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()

    // Filter for *valid finite numeric* coords. Bare `!= null` let
    // through Postgres NUMERIC values that deserialise as "NaN" / JS NaN
    // (crashes Leaflet with y1=NaN). But `Number(null)` is 0, so we
    // also need to reject null explicitly — otherwise un-geocoded
    // places like Epe (Gld) with NULL coords land at (0, 0), the
    // "null island" in the Gulf of Guinea.
    const withCoords = filteredPlaces.filter(p => {
      if (p.latitude == null || p.longitude == null) return false
      const lat = Number(p.latitude)
      const lng = Number(p.longitude)
      return Number.isFinite(lat) && Number.isFinite(lng) && p.inWindow > 0
    })

    const maxMentions = Math.max(1, ...withCoords.map(p => p.inWindow))

    for (const p of withCoords) {
      const dominantEvent = (p.event_types || []).filter(Boolean)[0] || 'other'
      const color = EVENT_COLOR[dominantEvent] || EVENT_COLOR.other
      // sqrt scaling so one hub doesn't dwarf everyone.
      const r = 6 + Math.round(Math.sqrt(p.inWindow / maxMentions) * 16)

      const marker = L.circleMarker([Number(p.latitude), Number(p.longitude)], {
        radius: r,
        fillColor: color,
        fillOpacity: 0.65,
        color: '#ffffff',
        weight: 2,
      })
      marker.bindTooltip(
        `<strong>${escapeHtml(cleanPlaceName(p.name_raw))}</strong><br>` +
        `${p.inWindow} mention${p.inWindow === 1 ? '' : 's'}` +
        (p.earliest_year ? ` · ${p.earliest_year}–${p.latest_year}` : ''),
        { direction: 'top', offset: [0, -r] }
      )
      marker.on('click', () => setSel(p.id))
      marker.addTo(layer)
    }

    if (withCoords.length > 0) {
      const bounds = L.latLngBounds(withCoords.map(p => [Number(p.latitude), Number(p.longitude)]))
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 })
    }
  }, [filteredPlaces])

  if (error) return <div className="form-error" style={{ margin: '2rem' }}>{error}</div>
  if (places === null) return <div className="loading" style={{ margin: '3rem', textAlign: 'center' }}>Loading…</div>

  if (places.length === 0) {
    return (
      <div className="empty-state" style={{ padding: '3rem 1rem' }}>
        <div className="empty-state__icon">🗺️</div>
        <h3>No places recorded yet</h3>
        <p>
          Upload a few documents and analyse them with AI — every place the
          AI finds (birth villages, marriage towns, archive cities) appears
          here as a pin on the map.
        </p>
      </div>
    )
  }

  const pendingGeocode = places.filter(p => p.geocode_status === 'pending').length

  return (
    <div className="places-view places-view--map">
      <div className="places-view__header">
        <div className="page-header__label">Family geography</div>
        <h2 className="places-view__title">Where your family has lived</h2>
        <p className="places-view__hint">
          Each pin is a place that appears in your documents. Radius grows
          with mention count in the selected period. Drag the slider to
          watch where the family lived across the generations.
        </p>
        {pendingGeocode > 0 && (
          <p className="places-view__pending">
            {pendingGeocode} place{pendingGeocode === 1 ? ' is' : 's are'} still
            being geocoded. Refresh in a minute to see them appear.
          </p>
        )}
      </div>

      {globalMin != null && globalMax != null && globalMin !== globalMax && (
        <TimeSlider
          min={globalMin}
          max={globalMax}
          start={windowStart}
          end={windowEnd}
          onChange={(s, e) => { setWS(s); setWE(e) }}
        />
      )}

      <div className="places-view__split">
        <div ref={mapRef} className="places-view__map" />
        <div className="places-view__list">
          {filteredPlaces.filter(p => p.inWindow > 0).map(p => (
            <PlaceCard
              key={p.id}
              place={p}
              treeId={treeId}
              selected={selectedId === p.id}
              onSelect={() => {
                // Toggle: click a card to expand it; click again to collapse.
                // Always pan the map to the place when expanding.
                const becomesSelected = selectedId !== p.id
                setSel(becomesSelected ? p.id : null)
                if (becomesSelected && p.latitude && p.longitude && mapObjRef.current) {
                  mapObjRef.current.setView([p.latitude, p.longitude], 11)
                }
              }}
              globalMin={globalMin}
              globalMax={globalMax}
            />
          ))}
          {filteredPlaces.every(p => p.inWindow === 0) && (
            <div className="places-view__empty-list">
              No mentions in this time window.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function TimeSlider({ min, max, start, end, onChange }) {
  const lo = Math.floor(min / 10) * 10
  const hi = Math.ceil(max / 10) * 10
  const [s, setS] = useState(start ?? lo)
  const [e, setE] = useState(end   ?? hi)

  useEffect(() => {
    const id = setTimeout(() => {
      onChange(s === lo ? null : s, e === hi ? null : e)
    }, 120)
    return () => clearTimeout(id)
  }, [s, e, lo, hi, onChange])

  return (
    <div className="places-view__period">
      <div className="places-view__period-label">
        <span>{s}</span>
        <span className="places-view__period-range-dash">—</span>
        <span>{e}</span>
        {(s !== lo || e !== hi) && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: '0.75rem', padding: '0.1rem 0.5rem' }}
            onClick={() => { setS(lo); setE(hi) }}
          >
            Reset
          </button>
        )}
      </div>
      <div className="places-view__period-sliders">
        <input type="range" min={lo} max={hi} step={5} value={s}
          onChange={ev => setS(Math.min(parseInt(ev.target.value, 10), e - 5))}
        />
        <input type="range" min={lo} max={hi} step={5} value={e}
          onChange={ev => setE(Math.max(parseInt(ev.target.value, 10), s + 5))}
        />
      </div>
    </div>
  )
}

// Clean up ", ,  ," sequences from place names that arrived with empty
// fields (common in GEDCOM imports that stringify city/region/country
// even when some levels are missing). Also trims leading/trailing commas.
function cleanPlaceName(raw) {
  if (!raw) return ''
  return String(raw)
    .replace(/\s*,\s*(?=,)/g, '')   // collapse ", ," → ","
    .replace(/^\s*,+\s*/, '')       // leading commas
    .replace(/\s*,+\s*$/, '')       // trailing commas
    .replace(/\s+,/g, ',')          // space before comma
    .replace(/,\s+/g, ', ')         // exactly one space after comma
    .trim()
}

function PlaceCard({ place, treeId, selected, onSelect, globalMin, globalMax }) {
  const events = (place.event_types || []).filter(Boolean)
  const range = place.earliest_year && place.latest_year
    ? place.earliest_year === place.latest_year
      ? `${place.earliest_year}`
      : `${place.earliest_year} – ${place.latest_year}`
    : null

  let left = null, width = null
  if (globalMin != null && globalMax != null && place.earliest_year && place.latest_year) {
    const span = Math.max(1, globalMax - globalMin)
    left  = ((place.earliest_year - globalMin) / span) * 100
    width = Math.max(2, ((place.latest_year - place.earliest_year) / span) * 100)
  }

  const notGeocoded = place.geocode_status !== 'ok'

  return (
    <div className={`place-card ${selected ? 'place-card--selected' : ''}`}>
      {/* Click the header to expand/collapse. Using a div + role="button"
          instead of <button> — nested <div> children inside a real
          <button> can collapse to zero height in some browser/CSS combos
          (we hit this on Safari + Chromium). Keyboard handler keeps it
          accessible. */}
      <div
        role="button"
        tabIndex={0}
        className="place-card__header"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect() }
        }}
        aria-expanded={selected}
      >
        <div className="place-card__row">
          <div className="place-card__name">
            {cleanPlaceName(place.name_raw)}
            {notGeocoded && <span className="place-card__no-geo" title="Couldn't place on the map yet">⊘</span>}
          </div>
          <div className="place-card__count">
            <span aria-hidden="true" className={`place-card__chevron${selected ? ' is-open' : ''}`}>▸</span>
            {place.inWindow} mention{place.inWindow === 1 ? '' : 's'}
          </div>
        </div>

        {range && <div className="place-card__range">{range}</div>}

        {left != null && (
          <div className="place-card__timeline">
            <div
              className="place-card__timeline-bar"
              style={{ left: `${left}%`, width: `${width}%` }}
            />
          </div>
        )}

        {events.length > 0 && (
          <div className="place-card__events">
            {events.map(ev => (
              <span
                key={ev}
                className="place-card__event"
                style={{ borderColor: EVENT_COLOR[ev] || '#9ca3af', color: EVENT_COLOR[ev] || '#9ca3af' }}
              >
                {EVENT_LABEL[ev] || ev}
              </span>
            ))}
          </div>
        )}
      </div>

      {selected && <PlacePersons treeId={treeId} placeId={place.id} />}
    </div>
  )
}

// Lazy-loaded panel that fetches the persons mentioned at this place
// when the card first opens. Cached for the lifetime of the component
// instance — closing + reopening doesn't refetch.
function PlacePersons({ treeId, placeId }) {
  const [persons, setPersons] = useState(null)
  const [error, setError]     = useState(null)

  useEffect(() => {
    let cancelled = false
    setPersons(null); setError(null)
    api.get(`/trees/${treeId}/kg/places/${placeId}/persons`)
      .then(p => { if (!cancelled) setPersons(Array.isArray(p) ? p : []) })
      .catch(err => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [treeId, placeId])

  if (error) {
    return <div className="place-card__persons place-card__persons--error">{error}</div>
  }
  if (persons === null) {
    return <div className="place-card__persons place-card__persons--loading">Loading…</div>
  }
  if (persons.length === 0) {
    return <div className="place-card__persons place-card__persons--empty">No persons indexed at this place yet.</div>
  }

  return (
    <ul className="place-card__persons">
      {persons.map(p => {
        const name = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unnamed'
        // Always route to the in-app tree/person view so the user can
        // edit directly — the public SEO page is read-only. Opens in a
        // new tab so the user keeps their place on the list.
        const href = `/tree/${treeId}/person/${p.id}`
        const by = (p.birth_date || '').slice(0, 4) || null
        const dy = (p.death_date || '').slice(0, 4) || null
        const years = by ? (dy ? `${by} – ${dy}` : `b. ${by}`) : dy ? `d. ${dy}` : null
        const evs = (p.event_types || []).filter(Boolean)
        return (
          <li key={p.id} className="place-card__person">
            <a href={href} target="_blank" rel="noopener noreferrer" className="place-card__person-link">
              <span className="place-card__person-name">{name}</span>
              {years && <span className="place-card__person-years">{years}</span>}
              {evs.length > 0 && (
                <span className="place-card__person-events">
                  {evs.map(ev => (
                    <span key={ev} className="place-card__person-event" style={{ color: EVENT_COLOR[ev] || '#9ca3af' }}>
                      {EVENT_LABEL[ev] || ev}
                    </span>
                  ))}
                </span>
              )}
            </a>
          </li>
        )
      })}
    </ul>
  )
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
